import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Tooltip,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import { useNotification } from '../../providers/NotificationProvider';
import {
  MailBlock,
  MailBlockType,
  newBlock,
  uploadMailImage,
  isDriveLink,
  MAIL_IMAGE_MAX_BYTES,
} from '../../hooks/useMailDelivery';

/**
 * 本文ブロックの編集部品 (ブロック組み立て方式。設計提案 §2.1)。
 *
 * ブロック種別は 文章 / 画像 / 区切り線 / ボタン / HTML の 5 つ (サーバー側 render_block と同じ)。
 * 並べ替えは上下ボタン、追加は下部のボタン群。HTML はシステム側で固定し、オペレーターは
 * 文言・画像 URL・リンク先・並び順だけを変える。差し込みコードは {{recipient_name}} など。
 *
 * 画像は「画像をアップロード」またはドラッグ＆ドロップで登録する。サーバー側 (mail_asset_upload) で
 * 幅 1200px へ縮小・撮影位置などのメタデータを除去し、公開バケットへ置いた URL が自動で入る。
 * Google Drive の共有リンクは画像として表示できないため、入力欄でエラー表示する (保存時もサーバーで弾く)。
 */

export const BLOCK_TYPE_LABEL: Record<MailBlockType, string> = {
  text: '文章',
  image: '画像',
  line: '区切り線',
  button: 'ボタン',
  html: 'HTML',
};

const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' }, '& .MuiInputLabel-root': { fontSize: '13px' } };

function BlockIcon({ type }: { type: MailBlockType }) {
  const sx = { fontSize: 18 };
  switch (type) {
    case 'text':
      return <Icons.Description sx={sx} />;
    case 'image':
      return <Icons.Image sx={sx} />;
    case 'button':
      return <Icons.OpenInNew sx={sx} />;
    case 'html':
      return <Icons.Code sx={sx} />;
    case 'line':
    default:
      return <Icons.ArrowDownward sx={sx} />;
  }
}

type BlockFieldsProps = {
  block: MailBlock;
  onChange: (patch: Partial<MailBlock>) => void;
  campaignId?: number | null;
  disabled?: boolean;
};

const fmtKb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

/** 画像ブロック (URL 入力 + アップロード + ドラッグ＆ドロップ) */
function ImageBlockFields({ block, onChange, campaignId, disabled }: BlockFieldsProps) {
  const notify = useNotification();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // アップロード完了時に最新のブロック配列へ反映するため、最新の onChange を参照する
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const str = (k: keyof MailBlock) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ [k]: e.target.value });
  const num = (k: keyof MailBlock) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ [k]: e.target.value === '' ? undefined : Number(e.target.value) });

  const handleFile = async (file: File | null | undefined) => {
    if (!file || uploading) return;
    if (file.size > MAIL_IMAGE_MAX_BYTES) {
      notify.error('画像が大きすぎます (20MB まで)');
      return;
    }
    setUploading(true);
    const r = await uploadMailImage(file, campaignId);
    setUploading(false);
    if (r.ok) {
      onChangeRef.current({ url: r.data.url });
      notify.success(`画像をアップロードしました (${fmtKb(r.data.original_bytes)} → ${fmtKb(r.data.bytes)}、${r.data.width}×${r.data.height})`);
    } else {
      notify.error(r.error);
    }
  };

  const drive = isDriveLink(block.url);
  const canDrop = !disabled && !uploading;

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}
      onDragOver={(e) => {
        if (!canDrop || !Array.from(e.dataTransfer.types).includes('Files')) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!canDrop) return;
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'start' }}>
        <TextField
          size="small"
          label="画像 URL"
          required
          value={block.url ?? ''}
          onChange={str('url')}
          placeholder="「画像をアップロード」で自動入力されます"
          error={drive}
          helperText={drive ? 'Google Drive のリンクは画像として表示できません。「画像をアップロード」から登録してください' : undefined}
          sx={fieldSx}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || uploading}
          startIcon={<Icons.CloudUpload sx={{ fontSize: 16 }} />}
          onClick={() => inputRef.current?.click()}
          sx={{ fontSize: 12, textTransform: 'none', height: 40, whiteSpace: 'nowrap' }}
        >
          画像をアップロード
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          hidden
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Box>
      <TextField size="small" label="クリック時のリンク先 (任意)" value={block.link ?? ''} onChange={str('link')} placeholder="https://..." sx={fieldSx} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', gap: 1 }}>
        <TextField size="small" label="代替テキスト" value={block.alt ?? ''} onChange={str('alt')} sx={fieldSx} />
        <TextField size="small" type="number" label="幅 (px)" value={block.width ?? 600} onChange={num('width')} sx={fieldSx} />
        <TextField size="small" select label="配置" value={block.align ?? 'center'} onChange={str('align')} sx={fieldSx}>
          <MenuItem value="left">左寄せ</MenuItem>
          <MenuItem value="center">中央</MenuItem>
          <MenuItem value="right">右寄せ</MenuItem>
        </TextField>
      </Box>
      <Box
        sx={{
          border: '1px dashed',
          borderColor: dragOver ? 'primary.main' : '#cbd5e1',
          bgcolor: dragOver ? 'rgba(0,127,64,0.06)' : 'transparent',
          borderRadius: 1,
          p: 1,
          textAlign: block.url && !uploading ? block.align ?? 'center' : 'center',
        }}
      >
        {uploading ? (
          <LoadingView message="アップロード中... (縮小して保存しています)" height={120} size={28} />
        ) : block.url && !drive ? (
          <img src={block.url} alt={block.alt ?? ''} style={{ maxWidth: '100%', maxHeight: 160, border: '1px solid #eee' }} />
        ) : (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', py: 2 }}>
            ここに画像をドラッグ＆ドロップ、または「画像をアップロード」 (JPEG / PNG / GIF / WebP、20MB まで)
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function BlockFields({ block, onChange, campaignId, disabled }: BlockFieldsProps) {
  const str = (k: keyof MailBlock) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ [k]: e.target.value });
  const num = (k: keyof MailBlock) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ [k]: e.target.value === '' ? undefined : Number(e.target.value) });

  switch (block.type) {
    case 'text':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          <TextField
            size="small"
            multiline
            minRows={3}
            label="文章"
            value={block.text ?? ''}
            onChange={str('text')}
            placeholder="{{company_name}} {{recipient_name}} 様 のように差し込みコードが使えます"
            sx={fieldSx}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '100px 120px 120px auto', gap: 1, alignItems: 'center' }}>
            <TextField size="small" type="number" label="文字サイズ" value={block.font_size ?? 14} onChange={num('font_size')} sx={fieldSx} />
            <TextField size="small" label="文字色" value={block.color ?? '#333333'} onChange={str('color')} sx={fieldSx} />
            <TextField size="small" select label="配置" value={block.align ?? 'left'} onChange={str('align')} sx={fieldSx}>
              <MenuItem value="left">左寄せ</MenuItem>
              <MenuItem value="center">中央</MenuItem>
              <MenuItem value="right">右寄せ</MenuItem>
            </TextField>
            <FormControlLabel
              control={<Checkbox size="small" checked={!!block.bold} onChange={(e) => onChange({ bold: e.target.checked })} />}
              label={<Typography sx={{ fontSize: 13 }}>太字</Typography>}
            />
          </Box>
        </Box>
      );
    case 'image':
      return <ImageBlockFields block={block} onChange={onChange} campaignId={campaignId} disabled={disabled} />;
    case 'line':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '140px 120px', gap: 1 }}>
          <TextField size="small" label="線の色" value={block.color ?? '#cccccc'} onChange={str('color')} sx={fieldSx} />
          <TextField size="small" type="number" label="太さ (px)" value={block.thickness ?? 1} onChange={num('thickness')} sx={fieldSx} />
        </Box>
      );
    case 'button':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
            <TextField size="small" label="ボタンの文言" value={block.label ?? ''} onChange={str('label')} sx={fieldSx} />
            <TextField size="small" label="リンク先 URL" required value={block.url ?? ''} onChange={str('url')} placeholder="https://..." sx={fieldSx} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '120px 120px 120px', gap: 1 }}>
            <TextField size="small" label="背景色" value={block.color ?? '#007F40'} onChange={str('color')} sx={fieldSx} />
            <TextField size="small" label="文字色" value={block.text_color ?? '#ffffff'} onChange={str('text_color')} sx={fieldSx} />
            <TextField size="small" select label="配置" value={block.align ?? 'center'} onChange={str('align')} sx={fieldSx}>
              <MenuItem value="left">左寄せ</MenuItem>
              <MenuItem value="center">中央</MenuItem>
              <MenuItem value="right">右寄せ</MenuItem>
            </TextField>
          </Box>
        </Box>
      );
    case 'html':
    default:
      return (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={4}
          maxRows={24}
          label="HTML ソース (上級者向け)"
          value={block.html ?? ''}
          onChange={str('html')}
          sx={{ ...fieldSx, '& .MuiInputBase-input': { fontSize: '12px', fontFamily: 'monospace' } }}
        />
      );
  }
}

type Props = {
  blocks: MailBlock[];
  onChange: (blocks: MailBlock[]) => void;
  disabled?: boolean;
  /** 画像の保存先フォルダに使う配信 ID (未保存の新規配信は null) */
  campaignId?: number | null;
};

const MailBlocksEditor: React.FC<Props> = ({ blocks, onChange, disabled, campaignId }) => {
  const update = (i: number, patch: Partial<MailBlock>) => {
    const next = blocks.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i: number) => onChange(blocks.filter((_, k) => k !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = (type: MailBlockType) => onChange([...blocks, newBlock(type)]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {blocks.length === 0 && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2, textAlign: 'center' }}>
          ブロックがありません。下のボタンから文章や画像を追加するか、テンプレートを読み込んでください。
        </Typography>
      )}
      {blocks.map((b, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.25, borderColor: '#e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: blueGrey[600] }}>
            <BlockIcon type={b.type} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1 }}>
              {i + 1}. {BLOCK_TYPE_LABEL[b.type]}
            </Typography>
            <Tooltip title="上へ">
              <span>
                <IconButton size="small" onClick={() => move(i, -1)} disabled={disabled || i === 0}>
                  <Icons.ArrowUpward sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="下へ">
              <span>
                <IconButton size="small" onClick={() => move(i, 1)} disabled={disabled || i === blocks.length - 1}>
                  <Icons.ArrowDownward sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="削除">
              <span>
                <IconButton size="small" onClick={() => remove(i)} disabled={disabled} color="error">
                  <Icons.Delete sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <BlockFields block={b} onChange={(patch) => update(i, patch)} campaignId={campaignId} disabled={disabled} />
        </Paper>
      ))}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pt: 0.5 }}>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', alignSelf: 'center', mr: 0.5 }}>ブロックを追加</Typography>
        {(Object.keys(BLOCK_TYPE_LABEL) as MailBlockType[]).map((t) => (
          <Button
            key={t}
            size="small"
            variant="outlined"
            disabled={disabled}
            startIcon={<Icons.Add sx={{ fontSize: 16 }} />}
            onClick={() => add(t)}
            sx={{ fontSize: 12, textTransform: 'none', py: 0.25 }}
          >
            {BLOCK_TYPE_LABEL[t]}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default MailBlocksEditor;
